import * as Yup from 'yup';

import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import {
  averageNonEVEmissions,
  mileageHardLimit,
} from '../../utils/netZeroHelper';
import { contentfulPropTypes, netZeroPropTypes } from '../../propTypes';
import { getVehicle, numberPlateRegex } from '../../api/DVLA';

import ErrorLabel from '../../ui/ErrorLabel';
import { LoginCard } from '../../ui';
import React from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const schema = Yup.object().shape({
  numberPlate: Yup.string()
    .matches(numberPlateRegex, 'Please enter a valid UK license plate')
    .required("Please enter your car's number plate"),
  annualMileage: Yup.number()
    .min(0, 'Please enter your annual mileage')
    .max(
      mileageHardLimit,
      "The value you've entered is very high compared to the average driver",
    )
    .required('Please enter your annual mileage'),
});

const StyledP = styled.p`
  margin-top: 10px;
  font-size: 12px;
  text-align: center;

  button.btn-link {
    padding: 0 4px 2px 1px;
    font-size: 12px;
  }
`;

const CarContainer = ({
  contentful,
  loading,
  fetched,
  cars,
  setCars,
  setCarError,
}) => {
  const navigate = useNavigate();

  const onSubmitCar = async ({ numberPlate, annualMileage }) => {
    cars.forEach((car, index, object) => {
      if (car.numberPlate === numberPlate) {
        object.splice(index, 1);
      }
    });
    if (numberPlate === 'AVERAGE') {
      setCars([
        {
          numberPlate: '---- ---',
          annualMileage: annualMileage,
          details: {
            co2Emissions: averageNonEVEmissions,
            fuelType: 'PETROL',
            make: 'AVERAGE CAR',
            registrationNumber: '---- ---',
          },
        },
        ...cars,
      ]);
      navigate('/net-zero/emissions');
    } else {
      const details = await getVehicle(numberPlate);
      if (!details || details?.message || !!details?.errors) {
        setCarError({
          annualMileage,
          numberPlate,
          error: details?.message || details?.errors[0],
        });
        navigate('/net-zero/car-not-found');
      } else {
        if (!details.co2Emissions) {
          details.co2Emissions = averageNonEVEmissions;
        }

        setCars([
          {
            numberPlate,
            annualMileage,
            details,
          },
          ...cars,
        ]);
        navigate('/net-zero/emissions');
      }
    }
  };

  const onSkipCar = () => {
    setCars([]);
    navigate('/net-zero/how-to-with-loop');
  };

  return (
    !loading &&
    fetched && (
      <LoginCard onBack={() => navigate('/net-zero/energy')}>
        <p>
          Step <strong>3</strong> of <strong>4</strong>
        </p>
        <h1 className="pb-3">{contentful.title}</h1>
        <p>{contentful?.subtitle}</p>
        <Formik
          onSubmit={onSubmitCar}
          initialValues={{
            numberPlate: '',
            annualMileage: '',
          }}
          validationSchema={schema}
          validateOnChange>
          {({ handleSubmit, isSubmitting, isValid, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Field name="numberPlate">
                {({ field, meta }) => (
                  <Form.Group controlId="numberPlate" className="text-left">
                    <Form.Label>What’s your number plate?</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={field.value}
                        onChange={e => {
                          e.target.value = e.target.value.toUpperCase();
                          field.onChange(e);
                        }}
                        onBlur={field.onBlur}
                        isValid={!meta.error && !!field.value}
                        placeholder="e.g. ABCD 1234"
                        className={cx({
                          error: !!meta.touched && !!meta.error,
                        })}
                      />
                    </InputGroup>
                    {meta.error && meta.touched && (
                      <ErrorLabel label={meta.error} />
                    )}
                    <StyledP>
                      If you don&apos;t know your car&apos;s number plate, click
                      <Button
                        onClick={() => setFieldValue('numberPlate', 'AVERAGE')}
                        variant="link">
                        here
                      </Button>
                      to continue with an average car.
                    </StyledP>
                  </Form.Group>
                )}
              </Field>
              <Field name="annualMileage">
                {({ field, meta }) => (
                  <Form.Group controlId="annualMileage" className="text-left">
                    <Form.Label>What’s your annual mileage?</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        value={field.value}
                        step="1000"
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isValid={!meta.error && !!field.value}
                        placeholder="e.g. 20,000"
                        className={cx({
                          error: !!meta.touched && !!meta.error,
                        })}
                      />
                    </InputGroup>
                    {meta.error && meta.touched && (
                      <ErrorLabel label={meta.error} />
                    )}
                  </Form.Group>
                )}
              </Field>
              <Button
                type="submit"
                variant="primary"
                disabled={!isValid}
                className="mb-3 w-100">
                {!isSubmitting ? (
                  contentful.primaryButtonText
                ) : (
                  <Spinner animation="border" size="sm" />
                )}
              </Button>
              <Button
                className="w-100"
                type="submit"
                variant="secondary"
                onClick={onSkipCar}>
                {contentful.secondaryButtonText}
              </Button>
            </Form>
          )}
        </Formik>
      </LoginCard>
    )
  );
};

CarContainer.propTypes = {
  ...contentfulPropTypes,
  ...netZeroPropTypes,
};

export default withContentfulTag(CarContainer, 'web_app_netzero_car_page');
